<template>
  <div class="ags-data-section">
    <div class="mb-3">
      <h3 class="text-md font-semibold text-gray-700">AGS Data</h3>
    </div>
    
    <div class="space-y-4">
      <!-- Selected Variables Display -->
      <div v-if="hasSelectedVariables" class="bg-green-50 border border-green-200 rounded-lg p-3">
        <div class="flex items-center justify-between mb-3">
          <h4 class="text-sm font-medium text-green-900">Selected Variables</h4>
          <button
            @click="clearAllVariables"
            class="text-xs text-green-600 hover:text-green-800 underline transition-colors"
          >
            Clear All
          </button>
        </div>
        
        <!-- Variable rows with scale direction, weight, and close button -->
        <div class="space-y-2">
          <div 
            v-for="variable in selectedVariables" 
            :key="variable.path"
            class="flex items-center gap-2 bg-white rounded-lg p-2 border border-green-200"
          >
            <!-- Variable Name (truncated) -->
            <div class="flex-1 min-w-0">
              <span class="text-sm text-gray-700 truncate block" :title="variable.label">
                {{ variable.label }}
              </span>
            </div>
            
            <!-- Scale Direction Toggle (shown for all variables) -->
            <button
              @click="toggleVariableScaleDirection(variable.path)"
              class="flex items-center justify-center w-6 h-6 rounded-full transition-colors flex-shrink-0"
              :class="getScaleDirection(variable.path) 
                ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                : 'bg-red-100 text-red-700 hover:bg-red-200'"
              :title="getScaleDirection(variable.path) ? 'Higher is Better' : 'Higher is Worse'"
            >
              <svg 
                class="w-4 h-4 transition-transform" 
                :class="{ 'rotate-180': !getScaleDirection(variable.path) }" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18"></path>
              </svg>
            </button>
            
            <!-- Weight Input (only for multi-variable mode) -->
            <div v-if="isMultiVariable" class="flex items-center gap-1 flex-shrink-0">
              <span class="text-xs text-gray-500">%</span>
              <input
                type="number"
                :value="getVariableWeight(variable.path)"
                @input="onWeightChange(variable.path, $event)"
                min="0"
                max="100"
                step="1"
                class="w-16 px-2 py-1 text-sm text-center border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
            </div>
            
            <!-- Remove Button -->
            <button
              @click="removeVariable(variable)"
              class="flex items-center justify-center w-6 h-6 rounded-full hover:bg-red-100 text-gray-500 hover:text-red-600 transition-colors flex-shrink-0"
              :title="`Remove ${variable.label}`"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>
      
      <!-- No Variables Selected State -->
      <div v-else class="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
        <svg class="mx-auto h-8 w-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
        </svg>
        <p class="text-sm text-gray-500 mb-3">No AGS variables selected</p>
      </div>
      
      <!-- Add/Edit Variables Button -->
      <button
        @click="openFilterModal"
        class="w-full flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
      >
        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
        </svg>
        {{ hasSelectedVariables ? 'Edit Variables' : 'Add AGS Filter(s)' }}
      </button>
      
      <!-- AGS Data Opacity Slider (shown when variables are selected) -->
      <div v-if="hasSelectedVariables" class="mt-4 pt-4 border-t border-gray-200">
        <div class="mb-3">
          <div class="flex items-center justify-between">
            <label class="text-sm font-medium text-gray-700">AGS Data Opacity</label>
            <span class="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">{{ opacityPercentage }}%</span>
          </div>
        </div>
        
        <div class="relative">
          <!-- Opacity Slider -->
          <input
            type="range"
            :value="agsDataOpacity"
            @input="onOpacityChange"
            min="0.1"
            max="1.0"
            step="0.05"
            class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer opacity-slider"
          >
          
          <!-- Slider Labels -->
          <div class="flex justify-between text-xs text-gray-500 mt-1">
            <span>10%</span>
            <span>50%</span>
            <span>100%</span>
          </div>
        </div>
        
        <!-- Quick Opacity Buttons -->
        <div class="flex gap-1 mt-3">
          <button
            v-for="preset in opacityPresets"
            :key="preset.value"
            @click="setOpacityPreset(preset.value)"
            class="flex-1 px-2 py-1 text-xs rounded transition-colors"
            :class="isActiveOpacityPreset(preset.value) 
              ? 'bg-green-100 text-green-800 border border-green-200' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
          >
            {{ preset.label }}
          </button>
        </div>
      </div>
      

    </div>
  </div>
</template>

<script>
import { agsStore } from '../../../composables/useAGSStore.js';

export default {
  name: 'Cresiteiq2AGSFilter',
  
  props: {
    // Current zoom level for determining available geographic levels
    currentZoom: {
      type: Number,
      default: 4
    }
  },
  
  data() {
    return {
      // AGS Store instance
      agsStore: null,
      
      // Display configuration
      maxDisplayedVariables: 3,
      
      // Opacity presets for quick selection
      opacityPresets: [
        { label: 'Light', value: 0.3 },
        { label: 'Medium', value: 0.5 },
        { label: 'Strong', value: 1.0 }
      ],
      
      // Variable weights (path -> percentage)
      variableWeights: {}
    }
  },
  
  created() {
    // Use singleton AGS store
    this.agsStore = agsStore;
  },
  
  computed: {
    // Get selected variables from store
    selectedVariables() {
      return this.agsStore ? this.agsStore.selectedVariables : [];
    },
    
    // Check if any variables are selected
    hasSelectedVariables() {
      return this.agsStore ? this.agsStore.hasSelectedVariables() : false;
    },
    
    // Check if multiple variables are selected (2+)
    isMultiVariable() {
      return this.selectedVariables.length >= 2;
    },
    
    // Get AGS data opacity from store
    agsDataOpacity() {
      return this.agsStore ? this.agsStore.agsDataOpacity : 0.5;
    },
    
    // Get opacity as percentage for display
    opacityPercentage() {
      return this.agsStore ? this.agsStore.getOpacityPercentage() : 50;
    },
    
    // Get variables to display (limited to maxDisplayedVariables)
    displayedVariables() {
      return this.selectedVariables.slice(0, this.maxDisplayedVariables);
    },
    
    // Get AGS scale reversed state from store
    agsScaleReversed() {
      return this.agsStore ? this.agsStore.agsScaleReversed : false;
    }
  },
  
  methods: {
    /**
     * Open the AGS filter modal
     */
    openFilterModal() {
      this.$emit('open-filter-modal');
    },
    
    /**
     * Remove a specific variable from selection
     * @param {Object} variable - Variable to remove
     */
    removeVariable(variable) {
      if (this.agsStore) {
        this.agsStore.removeVariable(variable);
        this.$emit('variables-updated', this.agsStore.selectedVariables);
      }
    },
    
    /**
     * Clear all selected variables
     */
    clearAllVariables() {
      if (this.agsStore) {
        this.agsStore.clearAllVariables();
        this.$emit('variables-updated', []);
      }
    },
    
    /**
     * Handle opacity slider change
     * @param {Event} event - Input event from opacity slider
     */
    onOpacityChange(event) {
      const newOpacity = parseFloat(event.target.value);
      if (this.agsStore) {
        this.agsStore.setAgsDataOpacity(newOpacity);
        this.$emit('opacity-changed', newOpacity);
      }
    },
    
    /**
     * Set opacity to a preset value
     * @param {Number} value - Preset opacity value
     */
    setOpacityPreset(value) {
      if (this.agsStore) {
        this.agsStore.setAgsDataOpacity(value);
        this.$emit('opacity-changed', value);
      }
    },
    
    /**
     * Check if a preset opacity value is currently active
     * @param {Number} presetValue - Preset value to check
     * @returns {Boolean} True if preset is active
     */
    isActiveOpacityPreset(presetValue) {
      return Math.abs(this.agsDataOpacity - presetValue) < 0.05;
    },
    
    /**
     * Toggle AGS scale direction between ascending and descending
     */
    toggleAgsScale() {
      if (this.agsStore) {
        this.agsStore.toggleAgsScale();
      }
    },
    
    /**
     * Get scale direction for a specific variable
     * @param {String} variablePath - Variable path
     * @returns {Boolean} True if higher is better, false if higher is worse
     */
    getScaleDirection(variablePath) {
      if (!this.agsStore) return false;
      return this.agsStore.scaleDirections[variablePath] ?? false;
    },
    
    /**
     * Toggle scale direction for a specific variable
     * @param {String} variablePath - Variable path
     */
    toggleVariableScaleDirection(variablePath) {
      if (this.agsStore) {
        this.agsStore.toggleScaleDirection(variablePath);
        this.$emit('scale-direction-changed', {
          variablePath,
          higherIsBetter: this.agsStore.scaleDirections[variablePath],
          isSingleVariable: !this.isMultiVariable
        });
      }
    },
    
    /**
     * Get weight for a specific variable
     * @param {String} variablePath - Variable path
     * @returns {Number} Weight as percentage (0-100)
     */
    getVariableWeight(variablePath) {
      if (!this.isMultiVariable) return 100;
      
      // Initialize weights if not set
      if (Object.keys(this.variableWeights).length === 0) {
        this.initializeWeights();
      }
      
      return Math.round((this.variableWeights[variablePath] || 0) * 100);
    },
    
    /**
     * Initialize equal weights for all variables
     */
    initializeWeights() {
      const count = this.selectedVariables.length;
      if (count === 0) return;
      
      const equalWeight = 1.0 / count;
      
      // Create new weights object for reactivity
      const newWeights = {};
      this.selectedVariables.forEach(variable => {
        newWeights[variable.path] = equalWeight;
      });
      
      // Replace entire object to trigger reactivity
      this.variableWeights = newWeights;
      
      // Update store
      if (this.agsStore) {
        this.agsStore.setWeights(newWeights);
      }
    },
    
    /**
     * Handle weight input change
     * @param {String} variablePath - Variable path
     * @param {Event} event - Input event
     */
    onWeightChange(variablePath, event) {
      const percentage = parseFloat(event.target.value);
      if (isNaN(percentage) || percentage < 0) return;
      
      // Convert percentage to decimal (0-1), clamped to 0-100%
      const newWeight = Math.min(100, Math.max(0, percentage)) / 100;
      
      // Get all other variables (excluding the one being changed)
      const otherVariables = this.selectedVariables.filter(v => v.path !== variablePath);
      
      if (otherVariables.length === 0) {
        // Only one variable, set to 100%
        this.$set(this.variableWeights, variablePath, 1.0);
      } else {
        // Calculate remaining weight to distribute among other variables
        const remainingWeight = 1.0 - newWeight;
        
        // Get current total weight of other variables
        const otherVariablesTotal = otherVariables.reduce((sum, v) => {
          return sum + (this.variableWeights[v.path] || 0);
        }, 0);
        
        // Update the changed variable's weight using Vue.set for reactivity
        this.$set(this.variableWeights, variablePath, newWeight);
        
        // Redistribute remaining weight proportionally among other variables
        if (otherVariablesTotal > 0) {
          // Proportional distribution based on current weights
          otherVariables.forEach(variable => {
            const currentWeight = this.variableWeights[variable.path] || 0;
            const proportion = currentWeight / otherVariablesTotal;
            this.$set(this.variableWeights, variable.path, remainingWeight * proportion);
          });
        } else {
          // Equal distribution if other variables have no weight
          const equalWeight = remainingWeight / otherVariables.length;
          otherVariables.forEach(variable => {
            this.$set(this.variableWeights, variable.path, equalWeight);
          });
        }
      }
      
      // Force Vue to update the view
      this.$forceUpdate();
      
      // Update store with new weights
      if (this.agsStore) {
        this.agsStore.setWeights(this.variableWeights);
      }
      
      // Emit weight change event
      this.$emit('weights-changed', this.variableWeights);
    },
    
    /**
     * Normalize weights to sum to 100%
     */
    normalizeWeights() {
      const total = Object.values(this.variableWeights).reduce((sum, w) => sum + w, 0);
      
      if (total === 0) {
        // If all weights are 0, reset to equal weights
        this.initializeWeights();
      } else if (Math.abs(total - 1.0) > 0.01) {
        // Normalize weights to sum to 1.0
        Object.keys(this.variableWeights).forEach(path => {
          this.variableWeights[path] = this.variableWeights[path] / total;
        });
      }
      
      // Emit normalized weights
      this.$emit('weights-changed', this.variableWeights);
    }
  },
  
  watch: {
    // Watch for variable changes to reinitialize weights
    selectedVariables: {
      handler(newVariables, oldVariables) {
        if (newVariables.length !== oldVariables.length) {
          this.initializeWeights();
        }
      },
      deep: true
    }
  }
}
</script>

<style scoped>
/* Custom opacity slider styling */
.opacity-slider {
  background: linear-gradient(to right, #e5e7eb 0%, #10b981 100%);
}

.opacity-slider::-webkit-slider-thumb {
  appearance: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #10b981;
  border: 2px solid #ffffff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  cursor: pointer;
}

.opacity-slider::-moz-range-thumb {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #10b981;
  border: 2px solid #ffffff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  cursor: pointer;
}

/* Transition animations */
.transition-colors {
  transition-property: color, background-color, border-color;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}

/* Hover effects for variable chips */
.group:hover .hover\:bg-green-200 {
  background-color: #bbf7d0;
}

/* Focus styles for accessibility */
button:focus {
  outline: 2px solid transparent;
  outline-offset: 2px;
}

button:focus-visible {
  outline: 2px solid #10b981;
  outline-offset: 2px;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .flex-wrap {
    gap: 0.25rem;
  }
  
  .px-2 {
    padding-left: 0.375rem;
    padding-right: 0.375rem;
  }
}
</style>